import { Dispatch } from "redux";

import {
	CustomView,
	CustomViewResponse,
	CustomViewsMetadata,
	ListPagePostRequest
} from "@/Contracts/UI";
import { GetLibraryResponse } from "@/Contracts/Users";
import { ZomentumEntities } from "@/Contracts";
import { GlobalState } from "@/Reducers/interface";
import { getLibraryListPost } from "@/Services/User";
import {
	getAllViewsForEntity,
	getViewMetadataForEntity,
	createViewForEntity,
	updateViewForEntity,
	deleteViewForEntity
} from "@/Services/Views";
import { areFiltersChanged } from "@/Utils";

export enum SavedLayoutsActionTypes {
	SHOW_SAVED_LAYOUTS_LOADER = "SAVED_LAYOUTS_SHOW_LOADER",
	HIDE_SAVED_LAYOUTS_LOADER = "SAVED_LAYOUTS_HIDE_LOADER",

	FETCH_SAVED_LAYOUTS_REQUESTED = "SAVED_LAYOUTS_FETCH_REQUESTED",
	FETCH_SAVED_LAYOUTS_SUCCESSFUL = "SAVED_LAYOUTS_FETCH_SUCCESSFUL",
	FETCH_SAVED_LAYOUTS_FAILED = "SAVED_LAYOUTS_FETCH_FAILED",

	FETCH_SAVED_LAYOUTS_VIEWS_SUCCESSFUL = "SAVED_LAYOUTS_FETCH_VIEWS_SUCCESSFUL",
	FETCH_SAVED_LAYOUTS_VIEW_METADATA_SUCCESSFUL = "SAVED_LAYOUTS_FETCH_VIEW_METADATA_SUCCESSFUL",

	CREATE_SAVED_LAYOUTS_VIEW_SUCCESSFUL = "SAVED_LAYOUTS_CREATE_VIEW_SUCCESSFUL",
	UPDATE_SAVED_LAYOUTS_VIEW_SUCCESSFUL = "SAVED_LAYOUTS_UPDATE_VIEW_SUCCESSFUL",
	DELETE_SAVED_LAYOUTS_VIEW_SUCCESSFUL = "SAVED_LAYOUTS_DELETE_VIEW_SUCCESSFUL",

	UPDATE_SAVED_LAYOUTS_CURRENT_ACTIVE_VIEW = "SAVED_LAYOUTS_UPDATE_CURRENT_ACTIVE_VIEW",
	RESET_SAVED_LAYOUTS_CURRENT_ACTIVE_VIEW = "SAVED_LAYOUTS_RESET_CURRENT_ACTIVE_VIEW"
}

interface ShowSavedLayoutsLoader {
	type: SavedLayoutsActionTypes.SHOW_SAVED_LAYOUTS_LOADER;
}
interface HideSavedLayoutsLoader {
	type: SavedLayoutsActionTypes.HIDE_SAVED_LAYOUTS_LOADER;
}
interface FetchSavedLayoutsRequested {
	type: SavedLayoutsActionTypes.FETCH_SAVED_LAYOUTS_REQUESTED;
}
interface FetchSavedLayoutsSuccessful {
	type: SavedLayoutsActionTypes.FETCH_SAVED_LAYOUTS_SUCCESSFUL;
	layoutList: GetLibraryResponse;
}
interface FetchSavedLayoutsFailed {
	type: SavedLayoutsActionTypes.FETCH_SAVED_LAYOUTS_FAILED;
}
interface FetchSavedLayoutsViewsSuccessful {
	type: SavedLayoutsActionTypes.FETCH_SAVED_LAYOUTS_VIEWS_SUCCESSFUL;
	viewList: CustomViewResponse;
}
interface FetchSavedLayoutsViewMetadataSuccessful {
	type: SavedLayoutsActionTypes.FETCH_SAVED_LAYOUTS_VIEW_METADATA_SUCCESSFUL;
	metadata: CustomViewsMetadata;
}
interface CreateSavedLayoutsViewSuccessful {
	type: SavedLayoutsActionTypes.CREATE_SAVED_LAYOUTS_VIEW_SUCCESSFUL;
	view: CustomView;
}
interface UpdateSavedLayoutsViewSuccessful {
	type: SavedLayoutsActionTypes.UPDATE_SAVED_LAYOUTS_VIEW_SUCCESSFUL;
	view: CustomView;
}
interface DeleteSavedLayoutsViewSuccessful {
	type: SavedLayoutsActionTypes.DELETE_SAVED_LAYOUTS_VIEW_SUCCESSFUL;
	deletedView: CustomView;
	initialView: CustomView;
}
interface UpdateSavedLayoutsCurrentActiveView {
	type: SavedLayoutsActionTypes.UPDATE_SAVED_LAYOUTS_CURRENT_ACTIVE_VIEW;
	currentActiveView: CustomView;
	isViewChanged: boolean;
}
interface ResetSavedLayoutsCurrentActiveView {
	type: SavedLayoutsActionTypes.RESET_SAVED_LAYOUTS_CURRENT_ACTIVE_VIEW;
}

export type SavedLayoutsActions =
	| ShowSavedLayoutsLoader
	| HideSavedLayoutsLoader
	| FetchSavedLayoutsRequested
	| FetchSavedLayoutsSuccessful
	| FetchSavedLayoutsFailed
	| FetchSavedLayoutsViewsSuccessful
	| FetchSavedLayoutsViewMetadataSuccessful
	| CreateSavedLayoutsViewSuccessful
	| UpdateSavedLayoutsViewSuccessful
	| DeleteSavedLayoutsViewSuccessful
	| UpdateSavedLayoutsCurrentActiveView
	| ResetSavedLayoutsCurrentActiveView;

let nextSavedLayoutsRequestId = 0;
let latestActiveSavedLayoutsRequestId = 0;

export const SavedLayoutsActionCreators = {
	showSavedLayoutsLoader: () => {
		return (dispatch: Dispatch): ShowSavedLayoutsLoader =>
			dispatch({
				type: SavedLayoutsActionTypes.SHOW_SAVED_LAYOUTS_LOADER
			});
	},

	hideSavedLayoutsLoader: () => {
		return (dispatch: Dispatch): HideSavedLayoutsLoader =>
			dispatch({
				type: SavedLayoutsActionTypes.HIDE_SAVED_LAYOUTS_LOADER
			});
	},

	fetchSavedLayouts: (layoutRequest: ListPagePostRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<FetchSavedLayoutsSuccessful | void> => {
			const requestId = ++nextSavedLayoutsRequestId;
			latestActiveSavedLayoutsRequestId = requestId;
			dispatch({
				type: SavedLayoutsActionTypes.FETCH_SAVED_LAYOUTS_REQUESTED
			});
			try {
				const response = await getLibraryListPost(layoutRequest);
				if (requestId !== latestActiveSavedLayoutsRequestId) {
					return;
				}
				const successful: FetchSavedLayoutsSuccessful = {
					type: SavedLayoutsActionTypes.FETCH_SAVED_LAYOUTS_SUCCESSFUL,
					layoutList: response
				};
				return dispatch(successful);
			} catch (error) {
				if (requestId !== latestActiveSavedLayoutsRequestId) {
					return;
				}
				dispatch({
					type: SavedLayoutsActionTypes.FETCH_SAVED_LAYOUTS_FAILED
				});
				return Promise.reject(error);
			}
		};
	},

	fetchAllSavedLayoutsViews: () => {
		return async (
			dispatch: Dispatch
		): Promise<FetchSavedLayoutsViewsSuccessful> => {
			try {
				const response = await getAllViewsForEntity(
					ZomentumEntities.LibraryResource
				);
				const successful: FetchSavedLayoutsViewsSuccessful = {
					type: SavedLayoutsActionTypes.FETCH_SAVED_LAYOUTS_VIEWS_SUCCESSFUL,
					viewList: response
				};
				return dispatch(successful);
			} catch (error) {
				console.error(error);
				return Promise.reject(error);
			}
		};
	},

	fetchSavedLayoutsViewMetadata: () => {
		return async (
			dispatch: Dispatch
		): Promise<FetchSavedLayoutsViewMetadataSuccessful> => {
			try {
				const response = await getViewMetadataForEntity(
					ZomentumEntities.LibraryResource
				);
				const successful: FetchSavedLayoutsViewMetadataSuccessful = {
					type: SavedLayoutsActionTypes.FETCH_SAVED_LAYOUTS_VIEW_METADATA_SUCCESSFUL,
					metadata: response
				};
				return dispatch(successful);
			} catch (error) {
				console.error(error);
				return Promise.reject(error);
			}
		};
	},

	updateCurrentActiveView: (
		selectedCurrentActiveView: CustomView,
		discardingView?: boolean
	) => {
		return (
			dispatch: Dispatch,
			getState: () => GlobalState
		): UpdateSavedLayoutsCurrentActiveView => {
			const currentActiveView = getState().savedLayouts.currentActiveView;
			const prevViewData = getState().savedLayouts.viewList?.data.find(
				view => view.id === selectedCurrentActiveView?.id
			);

			const isSameView =
				currentActiveView?.id === selectedCurrentActiveView?.id;

			if (isSameView && prevViewData?.id) {
				const hasFiltersChanged = areFiltersChanged(
					prevViewData.filter_criteria,
					selectedCurrentActiveView.filter_criteria
				);
				const hasColumnsChanged =
					JSON.stringify(selectedCurrentActiveView.columns) !==
					JSON.stringify(prevViewData.columns);

				return dispatch({
					type: SavedLayoutsActionTypes.UPDATE_SAVED_LAYOUTS_CURRENT_ACTIVE_VIEW,
					currentActiveView: selectedCurrentActiveView,
					isViewChanged: discardingView
						? false
						: hasFiltersChanged || hasColumnsChanged
				});
			}

			return dispatch({
				type: SavedLayoutsActionTypes.UPDATE_SAVED_LAYOUTS_CURRENT_ACTIVE_VIEW,
				currentActiveView: selectedCurrentActiveView,
				isViewChanged: false
			});
		};
	},

	resetCurrentActiveView: () => {
		return (dispatch: Dispatch): ResetSavedLayoutsCurrentActiveView =>
			dispatch({
				type: SavedLayoutsActionTypes.RESET_SAVED_LAYOUTS_CURRENT_ACTIVE_VIEW
			});
	},

	createSavedLayoutsView: (view: CustomView) => {
		return async (
			dispatch: Dispatch
		): Promise<CreateSavedLayoutsViewSuccessful> => {
			try {
				const response = await createViewForEntity(view);
				const successful: CreateSavedLayoutsViewSuccessful = {
					type: SavedLayoutsActionTypes.CREATE_SAVED_LAYOUTS_VIEW_SUCCESSFUL,
					view: response
				};
				return dispatch(successful);
			} catch (error) {
				console.error(error);
				return Promise.reject(error);
			}
		};
	},

	updateSavedLayoutsView: (view: CustomView) => {
		return async (
			dispatch: Dispatch
		): Promise<UpdateSavedLayoutsViewSuccessful> => {
			try {
				const response = await updateViewForEntity(view);
				const successful: UpdateSavedLayoutsViewSuccessful = {
					type: SavedLayoutsActionTypes.UPDATE_SAVED_LAYOUTS_VIEW_SUCCESSFUL,
					view: response
				};
				return dispatch(successful);
			} catch (error) {
				console.error(error);
				return Promise.reject(error);
			}
		};
	},

	deleteSavedLayoutsView: (
		deleteView: CustomView,
		initialView: CustomView
	) => {
		return async (
			dispatch: Dispatch
		): Promise<DeleteSavedLayoutsViewSuccessful> => {
			try {
				await deleteViewForEntity(deleteView.id);
				const successful: DeleteSavedLayoutsViewSuccessful = {
					type: SavedLayoutsActionTypes.DELETE_SAVED_LAYOUTS_VIEW_SUCCESSFUL,
					deletedView: deleteView,
					initialView
				};
				return dispatch(successful);
			} catch (error) {
				console.error(error);
				return Promise.reject(error);
			}
		};
	}
};
